import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Link,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FiTrendingUp } from "react-icons/fi";
import { sendReferralInvites } from "../../services/referralsService";

// The server answers with one of these per address. Short labels: the card is
// narrow, and the member only needs to know whether to try something else.
const RESULT_LABELS = {
  sent: ["green", "Sent"],
  already_member: ["gray", "Already a member"],
  already_invited: ["gray", "Invited recently"],
  opted_out: ["orange", "Opted out"],
  invalid: ["red", "Not an address"],
  limit: ["orange", "Daily limit"],
  failed: ["red", "Failed"],
};

/**
 * "Help us grow" — an invitation composer small enough to live in the rail.
 * Type or paste addresses, press send, done: the member never leaves the
 * dashboard. The full page at /invite carries the personal link, the note
 * field and the history.
 */
export default function GrowCard({ cardBg, cardText, cardTitle, cardMuted, t }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const count = useMemo(
    () => text.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean).length,
    [text]
  );

  const handleSend = async () => {
    if (!count || sending) return;
    setSending(true);
    setResults(null);
    try {
      const res = await sendReferralInvites({ text, note: "" });
      setResults(res.results || []);
      if (res.sent > 0) {
        setText("");
        toast({
          title: t("grow.sent_toast", "{{count}} invitation(s) sent", {
            count: res.sent,
          }),
          status: "success",
          duration: 3000,
        });
      }
    } catch (e) {
      toast({ title: e.message, status: "error", duration: 4000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      bg={cardBg}
      color={cardText}
      borderRadius="ui-lg"
      boxShadow="ui-sm"
      borderWidth="1px"
      borderColor="border.default"
      p={4}
    >
      <HStack spacing={1.5} mb={1} color="gwbi.600">
        <Icon as={FiTrendingUp} boxSize={3.5} />
        <Text
          fontSize="2xs"
          fontWeight="bold"
          letterSpacing="0.16em"
          textTransform="uppercase"
        >
          {t("grow.eyebrow", "Help us grow")}
        </Text>
      </HStack>

      <Heading fontSize="md" lineHeight="1.2" color={cardTitle} mb={1}>
        {t("grow.title", "More members, better knowledge")}
      </Heading>
      <Text fontSize="xs" color={cardMuted} mb={3}>
        {t(
          "grow.subtitle",
          "Every member adds to what the Institute knows: more research, more answers, better learning for everyone. Invite the colleagues who should be here."
        )}
      </Text>

      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t(
          "grow.placeholder",
          "maria@university.edu, paolo@winery.it"
        )}
        rows={2}
        fontSize="sm"
        mb={2}
        bg="surface.canvas"
        borderColor="border.default"
        _hover={{ borderColor: "gwbi.300" }}
      />

      <HStack justify="space-between" spacing={2}>
        <Button
          size="sm"
          colorScheme="gwbi"
          isLoading={sending}
          isDisabled={!count}
          onClick={handleSend}
        >
          {count
            ? t("grow.send_count", "Invite {{count}}", { count })
            : t("grow.send", "Send invitations")}
        </Button>
        <Link
          as={RouterLink}
          to="/invite"
          fontSize="xs"
          color="gwbi.600"
          fontWeight="semibold"
          _hover={{ textDecoration: "underline" }}
        >
          {t("grow.your_link", "Your link")} →
        </Link>
      </HStack>

      {results && results.length > 0 && (
        <VStack align="stretch" spacing={1} mt={3}>
          {results.map((r) => {
            const [scheme, label] = RESULT_LABELS[r.status] || ["gray", r.status];
            return (
              <HStack key={r.email} justify="space-between" spacing={2}>
                <Text fontSize="xs" color={cardTitle} noOfLines={1}>
                  {r.email}
                </Text>
                <Badge
                  colorScheme={scheme}
                  variant="subtle"
                  fontSize="0.6rem"
                  flexShrink={0}
                >
                  {t(`invite.result.${r.status}`, label)}
                </Badge>
              </HStack>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
